import { getWell, updateWell } from '@/api/wells';
import { WellDetailWithHistory } from '@/types/well';
import { dateInputToIso, parseIsoToDateInput } from '@/utils/wellDates';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface WellDetailModalProps {
  open: boolean;
  onClose: () => void;
  wellId: string | null;
  canManage?: boolean;
  onWellUpdated?: () => void;
}

export const WellDetailModal: React.FC<WellDetailModalProps> = ({
  open,
  onClose,
  wellId,
  canManage = false,
  onWellUpdated,
}) => {
  const [well, setWell] = useState<WellDetailWithHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [shutDownDate, setShutDownDate] = useState('');

  useEffect(() => {
    if (open && wellId) {
      loadWell();
    }
  }, [open, wellId]);

  const resetForm = (wellData: WellDetailWithHistory) => {
    setName(wellData.name || '');
    setEntryDate(parseIsoToDateInput(wellData.entry_date));
    setShutDownDate(parseIsoToDateInput(wellData.shut_down_date));
  };

  const loadWell = async () => {
    if (!wellId) return;

    setLoading(true);
    setError(null);

    try {
      const wellData = await getWell(wellId);

      if (!wellData) {
        throw new Error('Well data is undefined');
      }

      setWell(wellData);
      resetForm(wellData);
      setEditMode(false);
    } catch (err) {
      console.error('Error loading well:', err);
      setError(err instanceof Error ? err.message : 'Failed to load well details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!well) return;

    setLoading(true);
    setError(null);

    try {
      await updateWell(well.id, {
        name,
        entry_date: dateInputToIso(entryDate),
        shut_down_date: dateInputToIso(shutDownDate),
      });

      const reloadedWell = await getWell(well.id);
      setWell(reloadedWell);
      resetForm(reloadedWell);
      setEditMode(false);
      onWellUpdated?.();
    } catch (err) {
      console.error('Error updating well:', err);
      setError(err instanceof Error ? err.message : 'Failed to update well');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (well) {
      resetForm(well);
    }
    setEditMode(false);
  };

  if (!well && !loading) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {editMode ? 'Edit Well' : well?.name ?? 'Well'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading && !well ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : well ? (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Well Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Entry Date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                disabled={!editMode}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shut Down Date"
                type="date"
                value={shutDownDate}
                onChange={(e) => setShutDownDate(e.target.value)}
                disabled={!editMode}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>

        {well && (
          <>
            {editMode ? (
              <>
                <Button onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={loading || !name.trim()}>
                  Save
                </Button>
              </>
            ) : canManage ? (
              <Button onClick={() => setEditMode(true)} variant="contained">
                Edit
              </Button>
            ) : null}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
